import { eachDayOfInterval, format, isValid, parse, parseISO } from 'date-fns'

export const DEFAULT_ORG_TIMEZONE = 'America/New_York'

const MAX_SPAN_DAYS = 366

const LOCAL_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/

export type EventTimeInput = string | number | Date | null | undefined

type ZonedParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

function toInstant(value: EventTimeInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) return isValid(value) ? value : null
  if (typeof value === 'number') {
    const fromNumber = new Date(value)
    return isValid(fromNumber) ? fromNumber : null
  }
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

function resolveTimeZone(timeZone: string | null | undefined): string {
  if (!timeZone) return DEFAULT_ORG_TIMEZONE
  try {
    new Intl.DateTimeFormat('en-US', { timeZone })
    return timeZone
  } catch {
    return DEFAULT_ORG_TIMEZONE
  }
}

function zonedParts(instant: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant)
  const read = (type: string): number => {
    const value = parts.find((part) => part.type === type)?.value
    return value ? Number(value) : 0
  }
  let hour = read('hour')
  if (hour === 24) hour = 0
  return {
    year: read('year'),
    month: read('month'),
    day: read('day'),
    hour,
    minute: read('minute'),
    second: read('second'),
  }
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

function zoneOffsetMs(instant: Date, timeZone: string): number {
  const { year, month, day, hour, minute, second } = zonedParts(instant, timeZone)
  const asUtc = Date.UTC(year, month - 1, day, hour, minute, second)
  return asUtc - instant.getTime()
}

export function eventDayKeyInZone(value: EventTimeInput, timeZone: string): string {
  const instant = toInstant(value)
  if (!instant) return ''
  const { year, month, day } = zonedParts(instant, resolveTimeZone(timeZone))
  return `${year}-${pad(month)}-${pad(day)}`
}

export function eventDayKeys(
  startsAt: EventTimeInput,
  endsAt: EventTimeInput,
  timeZone: string,
): string[] {
  const zone = resolveTimeZone(timeZone)
  const start = toInstant(startsAt)
  if (!start) return []
  const startKey = eventDayKeyInZone(start, zone)
  const end = toInstant(endsAt)
  if (!end || end.getTime() < start.getTime()) return [startKey]
  const endKey = eventDayKeyInZone(end, zone)
  const days = eachDayOfInterval({
    start: parse(startKey, 'yyyy-MM-dd', new Date()),
    end: parse(endKey, 'yyyy-MM-dd', new Date()),
  })
  if (days.length > MAX_SPAN_DAYS) return [startKey]
  return days.map((day) => format(day, 'yyyy-MM-dd'))
}

export function formatEventWhenInZone(value: EventTimeInput, timeZone: string): string {
  const instant = toInstant(value)
  if (!instant) return ''
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTimeZone(timeZone),
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(instant)
}

export function formatEventDateInZone(value: EventTimeInput, timeZone: string): string {
  const instant = toInstant(value)
  if (!instant) return ''
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTimeZone(timeZone),
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(instant)
}

export function formatEventTimeInZone(value: EventTimeInput, timeZone: string): string {
  const instant = toInstant(value)
  if (!instant) return ''
  return new Intl.DateTimeFormat('en-US', {
    timeZone: resolveTimeZone(timeZone),
    hour: 'numeric',
    minute: '2-digit',
  }).format(instant)
}

export function isoToZonedInput(value: EventTimeInput, timeZone: string): string {
  const instant = toInstant(value)
  if (!instant) return ''
  const { year, month, day, hour, minute } = zonedParts(instant, resolveTimeZone(timeZone))
  return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}`
}

export function zonedInputToIso(localValue: string, timeZone: string): string {
  const match = LOCAL_INPUT_PATTERN.exec(localValue)
  if (!match) return ''
  const zone = resolveTimeZone(timeZone)
  const [, year, month, day, hour, minute] = match
  const naiveUtc = Date.UTC(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    0,
  )
  const firstOffset = zoneOffsetMs(new Date(naiveUtc), zone)
  let instant = new Date(naiveUtc - firstOffset)
  const secondOffset = zoneOffsetMs(instant, zone)
  if (secondOffset !== firstOffset) {
    instant = new Date(naiveUtc - secondOffset)
  }
  return instant.toISOString()
}
